import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Tone from 'tone';
import { createPitchTracker } from '../../utils/pitchTracker';
import { buildSlots, sungFrames, estimateLag, refineLag, gradeTake, summarisePasses, latestByNote } from '../../utils/pitchGrading';
import { justTargets } from '../../utils/justIntonation';
import { TUNINGS } from '../../utils/tuning';
import { encodeWav, takeFileName } from '../../utils/wavFile';
import { downloadBlob } from '../../utils/mp3Recorder';
import { logGrading, spoken, barsOf } from './practiceLog';
import { watchGradeOverlay, watchGradeHover, watchRangeHover } from './gradeOverlay';
import { rangeView } from './rangeView';
import { openMicCapture, describeMicError } from './micCapture';
import { onAudioDeviceChange } from './audioDevices';
import { highlightedSection } from './syncedCapture';
import { sectionAudio, sectionStart, takeCovers } from '../../utils/syncedTake';
import { keyAtNote } from '../../utils/noteSpelling';

/**
 * Graded takes (desktop): sing along with playback and each note of my part
 * is graded for pitch, on the score.
 *
 * While grading is on, the mic is captured on Tone's own audio clock, so
 * every sample has a time on the clock the notes are scheduled on. Each
 * 10 ms pitch frame is placed in the score through the Transport's tick
 * position at that instant, which follows loops, pauses, jumps and live
 * tempo changes. A take runs from Play (always with a one-bar count-in) to
 * Stop; notes are graded as they finish and the grades stay on the score
 * until hidden or cleared. A new take replaces them.
 *
 * Grading is against equal temperament (the piano's tuning) or, as an
 * option, just intonation, where each note is tuned to the chord the other
 * parts are sounding (see justIntonation.js).
 */

// After Stop, keep listening this long: the last note reaches the mic late
const TAIL_MS = 600;
// How often grades are refreshed while singing
const REGRADE_MS = 400;
// Re-estimate the lag after this many new frames, from at most the last minute
const LAG_EVERY_FRAMES = 200;
const LAG_WINDOW_FRAMES = 6000;
// While singing, grade only the last 20 s again; the whole take at the end
const LIVE_WINDOW_FRAMES = 2000;
// Inspector: how long it waits for the pointer to reach it after leaving a
// note; the playback of a note is its singing and the consonants around it
// (see inspection in pitchGrading.js), faded in and out; how long the
// target tone sounds
const INSPECT_HIDE_MS = 300;
const LISTEN_MARGIN_SECONDS = 0.005;
// A pitch reading changes about this long after the sound does (the pitch
// tracker's window), so a note's sound starts that much before its first one
const PITCH_READING_DELAY_SECONDS = 0.02;
const LISTEN_FADE_SECONDS = 0.008;
const TARGET_TONE_SECONDS = 1.2;
// Audio kept for the download
const MAX_KEPT_SECONDS = 600;
// Download normalisation, as in the scratch recorder
const TARGET_PEAK = 0.89;
const MAX_BOOST = 12;
const SILENCE_FLOOR = 0.0005;

const MIC_ERROR = 'Microphone unavailable — check browser permissions';
const NO_NOTES = 'My part has no notes to grade here.';

// Per-device preference: grade against equal temperament or just intonation
const TUNING_KEY = 'musiker-grade-tuning';
const readTuning = () => {
  try { return localStorage.getItem(TUNING_KEY) === TUNINGS.just ? TUNINGS.just : TUNINGS.equal; } catch (e) { return TUNINGS.equal; }
};
const writeTuning = (tuning) => {
  try { localStorage.setItem(TUNING_KEY, tuning); } catch (e) { /* ignore */ }
};

// Per-device preference: grade while singing, or only once the take is over
const LIVE_KEY = 'musiker-grade-live';
const readLive = () => {
  try { return localStorage.getItem(LIVE_KEY) === 'on'; } catch (e) { return false; }
};
const writeLive = (on) => {
  try { localStorage.setItem(LIVE_KEY, on ? 'on' : 'off'); } catch (e) { /* ignore */ }
};

// The take's slots under a tuning: just intonation tunes each note of my
// part to the chord the other parts are sounding
const slotsFor = (take, tuning) => buildSlots(tuning === TUNINGS.just ? justTargets(take.notes, take.others) : take.notes);

const round = (value, places) => {
  const scale = 10 ** places;
  return Math.round(value * scale) / scale;
};

// Grades as a string, to skip redrawing the score when nothing changed
const signatureOf = (results) => results.map(r => `${r.pass}:${r.slot}:${r.grade}:${r.cents}`).join('|');

/** "Pass 2: 14 of 18 in tune" plus the rest of the breakdown. */
export function describePass(summary, tuning = TUNINGS.equal) {
  const headline = `Pass ${summary.pass + 1}${tuning === TUNINGS.just ? ' (just)' : ''}: ${summary.good} of ${summary.graded} in tune`;
  const parts = [];
  if (summary.average !== null) parts.push(`avg ${summary.average}¢ off`);
  if (summary.close) parts.push(`${summary.close} close`);
  if (summary.off) parts.push(`${summary.off} off`);
  if (summary.missed) parts.push(`${summary.missed} not heard`);
  if (summary.scooped) parts.push(`${summary.scooped} scooped`);
  if (summary.lean !== null && Math.abs(summary.lean) >= 5) {
    parts.push(summary.lean > 0 ? `leans sharp +${summary.lean}¢` : `leans flat −${-summary.lean}¢`);
  }
  return { headline, detail: parts.join(' · ') };
}

export function useGradedTakes({ active, controls, hasMe }) {
  const { user, fileName, isPlaying, isPaused, selectedPart, transposeSteps, getOsmd, hoverPlay } = controls;
  const inPractice = isPlaying || isPaused;

  const [enabled, setEnabledState] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [listening, setListening] = useState(false);
  const [livePass, setLivePass] = useState(0);
  const [results, setResults] = useState([]); // latest grade per notehead
  const [passes, setPasses] = useState([]); // per pass summaries of this take
  const [visible, setVisible] = useState(true);
  const [hasTake, setHasTake] = useState(false);
  const [tuning, setTuningState] = useState(readTuning);
  const tuningRef = useRef(tuning);
  const [live, setLiveState] = useState(readLive);
  const liveRef = useRef(live);
  const setLive = useCallback((on) => {
    liveRef.current = on;
    setLiveState(on);
    writeLive(on);
  }, []);

  const controlsRef = useRef(controls);
  controlsRef.current = controls;
  const micRef = useRef(null); // see openMicCapture
  const transportLogRef = useRef([]); // { time, type } of Transport start/stop/pause
  const takeRef = useRef(null);

  // ----- grading -----
  // `final`: the take is over (every pass is complete). `full`: grade the
  // whole take; otherwise, while singing, only the last LIVE_WINDOW_FRAMES
  // are graded again and earlier grades are kept.
  const regrade = useCallback((take, { final = false, full = final } = {}) => {
    const { frames, positions, slots, hopSeconds } = take;
    if (frames.length === 0) return;
    const sung = sungFrames(frames, hopSeconds);
    if (full || frames.length - take.lagCheckedAt >= LAG_EVERY_FRAMES) {
      const from = Math.max(0, frames.length - LAG_WINDOW_FRAMES);
      const recent = {
        frames: frames.slice(from),
        positions: positions.slice(from),
        slots,
        sung: sung.slice(from),
        hopSeconds
      };
      // Held pitches first, then the moves between notes pin it down
      const estimate = estimateLag({ ...recent, priorFrames: take.lagFrames });
      take.lagFrames = refineLag({ ...recent, lagFrames: estimate });
      take.lagCheckedAt = frames.length;
    }
    const from = full ? 0 : Math.max(0, frames.length - LIVE_WINDOW_FRAMES);
    const fresh = gradeTake({
      frames: frames.slice(from),
      positions: positions.slice(from),
      slots,
      sung: sung.slice(from),
      lagFrames: take.lagFrames,
      hopSeconds,
      final
    });
    let graded = fresh;
    if (from > 0) {
      const byKey = new Map(take.results.map(r => [`${r.pass}:${r.slot}`, r]));
      fresh.forEach(r => byKey.set(`${r.pass}:${r.slot}`, r));
      graded = [...byKey.values()].sort((a, b) => a.pass - b.pass || slots[a.slot].start - slots[b.slot].start);
    }
    take.results = graded;
    const signature = signatureOf(graded);
    if (signature === take.signature) return;
    take.signature = signature;
    // Each grade carries the key shown where its note is, to spell note names
    const c = controlsRef.current;
    const osmd = c.getOsmd?.();
    setResults(latestByNote(graded).map(r => ({ ...r, fifths: keyAtNote(osmd, r.ref, c.transposeSteps) })));
    setPasses(summarisePasses(graded));
  }, []);

  // Where playback was at context time t: { pass, time } or null. Walks the
  // Transport's start/stop/pause log in step with the frames; a loop wrap or
  // a fresh start (after a stop or a jump) begins a new pass.
  const positionAt = useCallback((take, t) => {
    const log = transportLogRef.current;
    while (take.cursor < log.length && log[take.cursor].time <= t) {
      const { type } = log[take.cursor++];
      if (type === 'start') {
        if (take.state === 'stopped') take.newRun = true;
        take.state = 'started';
      } else {
        take.state = type === 'pause' ? 'paused' : 'stopped';
      }
    }
    if (take.state !== 'started') return null;
    const ticks = Tone.Transport.getTicksAtTime(t);
    if (take.newRun || ticks < take.lastTicks) {
      take.pass++;
      take.newRun = false;
      setLivePass(take.pass);
    }
    take.lastTicks = ticks;
    return { pass: take.pass, time: controlsRef.current.audioHook.ticksToBakedTime(ticks) };
  }, []);

  const handleChunk = useCallback(({ samples, frame }) => {
    const take = takeRef.current;
    if (!take || take.ended || frame + samples.length <= take.firstFrame) return;
    if (take.kept < MAX_KEPT_SECONDS * take.sampleRate) {
      take.chunks.push({ frame, samples });
      take.kept += samples.length;
    }
    for (const result of take.tracker.push(samples, frame)) {
      take.frames.push(result);
      take.positions.push(positionAt(take, result.sample / take.sampleRate));
    }
    // Grading while singing is optional: its bursts of work can nudge
    // playback, so by default grades only come once the take is over
    const now = performance.now();
    if (liveRef.current && now - take.lastGradeAt >= REGRADE_MS) {
      take.lastGradeAt = now;
      regrade(take);
    }
  }, [positionAt, regrade]);

  const finishTake = useCallback((take) => {
    if (take.ended) return;
    take.ended = true;
    clearTimeout(take.finishTimer);
    micRef.current?.stop();
    regrade(take, { final: true });
    // One gain for playing back and downloading the take, as in the
    // scratch recorder: just under full scale, never chasing silence
    let peak = 0;
    take.chunks.forEach(({ samples }) => {
      for (let i = 0; i < samples.length; i++) peak = Math.max(peak, Math.abs(samples[i]));
    });
    take.gain = peak < SILENCE_FLOOR ? 1 : Math.min(MAX_BOOST, TARGET_PEAK / peak);
    setListening(false);
    setHasTake(take.chunks.length > 0);
    const summaries = summarisePasses(take.results || []);
    const last = summaries[summaries.length - 1];
    const c = controlsRef.current;
    const seconds = (Date.now() - take.startedAt) / 1000;
    const where = take.bars ? ` of ${take.bars}` : '';
    const outcome = last
      ? `${describePass(last, take.tuning).headline}${last.average !== null ? `, avg ${last.average}¢ off` : ''}`
      : 'nothing graded';
    logGrading({
      user: c.user,
      fileName: c.fileName,
      description: `Graded take${where} (${spoken(seconds)}, ${summaries.length} ${summaries.length === 1 ? 'pass' : 'passes'}): ${outcome}`,
      metadata: {
        event: 'graded_take',
        seconds: Math.round(seconds),
        bars: take.bars,
        passes: summaries.length,
        lagMs: Math.round(take.lagFrames * take.hopSeconds * 1000),
        tuning: take.tuning,
        live: liveRef.current,
        last: last ? { graded: last.graded, good: last.good, close: last.close, off: last.off, missed: last.missed, scooped: last.scooped, average: last.average, lean: last.lean } : null
      }
    });
  }, [regrade]);

  const beginTake = useCallback(() => {
    const mic = micRef.current;
    if (!mic) return;
    if (takeRef.current && !takeRef.current.ended) finishTake(takeRef.current);

    // The notes being played right now: the looping section's, or the song's
    const c = controlsRef.current;
    const audio = c.audioHook;
    const looping = c.isLoopMode && audio.loopEventsRef.current.length > 0;
    const events = looping ? audio.loopEventsRef.current : audio.noteEventsRef.current;
    const mine = String(c.selectedPart);
    const notes = [];
    const others = []; // every other part, for the chords just intonation tunes to
    events.forEach((event) => {
      if (event.pitch === 'REST') return;
      const midi = Tone.Frequency(event.pitch).toMidi();
      if (!Number.isFinite(midi)) return;
      const note = { start: event.time, end: event.time + event.duration, midi };
      if (String(event.partId) !== mine) others.push(note);
      else if (event.sourceNote) notes.push({ ...note, ref: event.sourceNote });
    });
    takeRef.current = null;
    setResults([]);
    setPasses([]);
    setHasTake(false);
    if (notes.length === 0) {
      setError(NO_NOTES);
      return;
    }
    setError(null);

    const context = Tone.getContext();
    const sampleRate = context.sampleRate;
    const tracker = createPitchTracker(sampleRate);
    // First guess at the lag until the take has enough singing to measure
    // it: the highlight-sync setting (the output latency the user tuned)
    // plus a typical input latency
    const output = Number.isFinite(c.audioLatency) && c.audioLatency > 0 ? c.audioLatency : 0.03;
    const now = context.currentTime;
    transportLogRef.current = transportLogRef.current.filter(event => event.time >= now - 0.5);
    const take = {
      notes,
      others,
      tuning: tuningRef.current,
      tracker,
      sampleRate,
      hopSeconds: tracker.hopSeconds,
      firstFrame: Math.floor(now * sampleRate),
      // Where in the song it is sung, for playing back one section of it
      bars: looping ? barsOf(c.selectedRange) : null,
      quarterSeconds: audio.ticksToBakedTime(Tone.Transport.PPQ),
      songOffset: sectionStart(events, looping, audio.ticksToBakedTime(Tone.Transport.PPQ)),
      frames: [],
      positions: [],
      chunks: [],
      kept: 0,
      cursor: 0,
      state: 'stopped',
      newRun: false,
      pass: -1,
      lastTicks: 0,
      lagFrames: Math.round(Math.min(0.3, output + 0.05) / tracker.hopSeconds),
      lagCheckedAt: 0,
      lastGradeAt: 0,
      signature: '',
      results: [],
      startedAt: Date.now(),
      ended: false,
      finishTimer: null
    };
    take.slots = slotsFor(take, take.tuning);
    takeRef.current = take;
    setLivePass(0);
    setVisible(true);
    setListening(true);
    mic.start();
  }, [finishTake]);

  // ----- mic -----
  const releaseMic = useCallback(() => {
    const mic = micRef.current;
    if (!mic) return;
    micRef.current = null;
    mic.close();
  }, []);

  const handleChunkRef = useRef(handleChunk);
  handleChunkRef.current = handleChunk;

  const enable = useCallback(async () => {
    setStarting(true);
    setError(null);
    try {
      micRef.current = await openMicCapture((chunk) => handleChunkRef.current(chunk));
      setEnabledState(true);
      return true;
    } catch (e) {
      setError(`${MIC_ERROR} (${describeMicError(e)})`);
      return false;
    } finally {
      setStarting(false);
    }
  }, []);

  const disable = useCallback(() => {
    const take = takeRef.current;
    if (take && !take.ended) finishTake(take);
    releaseMic();
    setEnabledState(false);
    setListening(false);
  }, [finishTake, releaseMic]);

  // A different mic chosen while grading is on: switch to it once no take
  // is being sung
  const [micStale, setMicStale] = useState(false);
  useEffect(() => onAudioDeviceChange((kind) => {
    if (kind === 'input' && micRef.current) setMicStale(true);
  }), []);
  useEffect(() => {
    if (!micStale || listening) return;
    setMicStale(false);
    if (!enabled || !micRef.current) return;
    releaseMic();
    enable();
  }, [micStale, listening, enabled, releaseMic, enable]);

  const setEnabled = useCallback(async (on) => {
    if (on) {
      const ok = await enable();
      if (ok) logGrading({ user, fileName, description: 'Turned Grade my pitch on', metadata: { event: 'grading_on', mic: micRef.current?.label || null } });
    } else {
      disable();
      logGrading({ user, fileName, description: 'Turned Grade my pitch off', metadata: { event: 'grading_off' } });
    }
  }, [enable, disable, user, fileName]);

  // Transport start/stop/pause times, so frames can tell when playback ran
  useEffect(() => {
    if (!enabled) return undefined;
    transportLogRef.current = [];
    const handlers = ['start', 'stop', 'pause'].map(type => [type, (time) => { transportLogRef.current.push({ time, type }); }]);
    handlers.forEach(([type, handler]) => Tone.Transport.on(type, handler));
    return () => handlers.forEach(([type, handler]) => Tone.Transport.off(type, handler));
  }, [enabled]);

  // A take runs from Play to Stop (pausing keeps it going)
  useEffect(() => {
    if (!enabled) return;
    const take = takeRef.current;
    if (inPractice) {
      if (!take || take.ended || take.finishTimer) beginTake();
    } else if (take && !take.ended && !take.finishTimer) {
      take.finishTimer = setTimeout(() => finishTake(take), TAIL_MS);
    }
  }, [enabled, inPractice, beginTake, finishTake]);

  // Grading needs my part; leaving practice turns it off
  useEffect(() => {
    if (enabled && (!active || !hasMe)) disable();
  }, [enabled, active, hasMe, disable]);

  // Grades belong to one song, part and key
  useEffect(() => {
    const take = takeRef.current;
    if (take && !take.ended) finishTake(take);
    takeRef.current = null;
    setResults([]);
    setPasses([]);
    setHasTake(false);
    setError(null);
  }, [fileName, selectedPart, transposeSteps, finishTake]);

  // Release the mic when the shell goes away
  useEffect(() => () => {
    const take = takeRef.current;
    if (take) clearTimeout(take.finishTimer);
    releaseMic();
  }, [releaseMic]);

  // ----- score overlay -----
  const getOsmdRef = useRef(getOsmd);
  getOsmdRef.current = getOsmd;
  const marksRef = useRef([]);
  useEffect(() => {
    const osmd = getOsmdRef.current?.();
    if (!osmd || !visible || results.length === 0) return undefined;
    return watchGradeOverlay(osmd, results, (marks) => { marksRef.current = marks; });
  }, [results, visible]);

  // Hovering over a graded note inspects it (not in hover-to-hear mode,
  // where hovering plays notes). Leaving the note gives the pointer a
  // moment to reach the inspector, which then stays open while the pointer
  // is on it, so its buttons can be used.
  const [inspect, setInspect] = useState(null);
  const inspecting = visible && results.length > 0 && !hoverPlay;
  const holdRef = useRef(false);
  const hideTimerRef = useRef(null);
  const hideSoon = useCallback(() => {
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      if (!holdRef.current) setInspect(null);
    }, INSPECT_HIDE_MS);
  }, []);
  const onHover = useCallback((hit) => {
    if (hit) {
      clearTimeout(hideTimerRef.current);
      setInspect(hit);
    } else {
      hideSoon();
    }
  }, [hideSoon]);
  const holdInspect = useCallback((on) => {
    holdRef.current = on;
    if (on) clearTimeout(hideTimerRef.current);
    else hideSoon();
  }, [hideSoon]);
  useEffect(() => {
    const osmd = getOsmdRef.current?.();
    if (!osmd || !inspecting) return undefined;
    return watchGradeHover(osmd, () => marksRef.current, onHover);
  }, [inspecting, fileName, onHover]);
  useEffect(() => {
    if (!inspecting) {
      holdRef.current = false;
      setInspect(null);
    }
  }, [inspecting]);

  // Listening from the inspector: the singing lined up with the note (a
  // little either side), or the target pitch. Only once the take is over,
  // so nothing plays over the song or into the recording.
  const [hearing, setHearing] = useState(null); // 'take' | 'target' | 'recording' | null
  const sourceRef = useRef(null);
  const toneRef = useRef(null);
  const targetTimerRef = useRef(null);
  const stopHearing = useCallback(() => {
    const source = sourceRef.current;
    if (source) {
      source.onended = null;
      try {
        source.stop();
      } catch (e) {
        // Already finished
      }
      sourceRef.current = null;
    }
    toneRef.current?.triggerRelease();
    setHearing(null);
  }, []);

  // Plays some of the take at its playback gain; `kind` is what `hearing` reports
  const playSamples = useCallback(async (take, samples, kind) => {
    await Tone.start();
    const context = Tone.getContext();
    const buffer = context.createBuffer(1, samples.length, take.sampleRate);
    buffer.copyToChannel(samples, 0);
    const source = context.createBufferSource();
    const gain = context.createGain();
    gain.gain.value = take.gain;
    source.buffer = buffer;
    source.connect(gain);
    gain.connect(context.rawContext.destination);
    source.onended = () => {
      if (sourceRef.current === source) {
        sourceRef.current = null;
        setHearing(null);
      }
      gain.disconnect();
    };
    sourceRef.current = source;
    setHearing(kind);
    source.start();
  }, []);

  const hearTake = useCallback(async (result) => {
    const take = takeRef.current;
    const range = result?.detail?.listen;
    if (!take || !take.ended || !range) return;
    stopHearing();
    const margin = Math.round(LISTEN_MARGIN_SECONDS * take.sampleRate);
    const from = range[0] - margin - Math.round(PITCH_READING_DELAY_SECONDS * take.sampleRate);
    const to = range[1] + margin;
    const snippet = new Float32Array(to - from);
    const covered = new Uint8Array(snippet.length);
    take.chunks.forEach(({ frame, samples }) => {
      const start = Math.max(from, frame);
      const end = Math.min(to, frame + samples.length);
      if (end > start) {
        snippet.set(samples.subarray(start - frame, end - frame), start - from);
        covered.fill(1, start - from, end - from);
      }
    });
    // The audio thread drops a few milliseconds now and then; left silent
    // those gaps click, so fill each with the sound just before it
    for (let i = 1; i < snippet.length; i++) {
      if (covered[i] || !covered[i - 1]) continue;
      let gapEnd = i;
      while (gapEnd < snippet.length && !covered[gapEnd]) gapEnd++;
      const length = gapEnd - i;
      if (i >= length) snippet.copyWithin(i, i - length, i);
      i = gapEnd;
    }
    // Short fades, so cutting the note out of the take does not click
    const fade = Math.min(snippet.length >> 2, Math.round(LISTEN_FADE_SECONDS * take.sampleRate));
    for (let i = 0; i < fade; i++) {
      snippet[i] *= i / fade;
      snippet[snippet.length - 1 - i] *= i / fade;
    }
    playSamples(take, snippet, 'take');
  }, [stopHearing, playSamples]);

  // Playing the take back, my voice alone: with a section highlighted only
  // my singing of that section, otherwise the whole recording
  const playRecording = useCallback(() => {
    const take = takeRef.current;
    if (!take || !take.ended || take.chunks.length === 0) return;
    stopHearing();
    const c = controlsRef.current;
    const section = highlightedSection(c);
    let samples = section && sectionAudio(take, section.from, section.to);
    const bars = samples ? barsOf(c.selectedRange) : null;
    logGrading({
      user: c.user,
      fileName: c.fileName,
      description: bars ? `Played back my singing of ${bars} (graded take)` : 'Played back the graded take',
      metadata: { event: 'play_take', section: Boolean(samples), bars }
    });
    if (!samples) {
      samples = new Float32Array(take.kept);
      let offset = 0;
      take.chunks.forEach((chunk) => {
        samples.set(chunk.samples, offset);
        offset += chunk.samples.length;
      });
    }
    playSamples(take, samples, 'recording');
  }, [stopHearing, playSamples]);

  const hearTarget = useCallback(async (result) => {
    if (!result || !takeRef.current?.ended) return;
    stopHearing();
    await Tone.start();
    if (!toneRef.current) {
      toneRef.current = new Tone.Synth({
        oscillator: { type: 'triangle' },
        envelope: { attack: 0.02, decay: 0.1, sustain: 0.8, release: 0.3 },
        volume: -10
      }).toDestination();
    }
    // The target exactly, just-tuned included (not the nearest piano key)
    toneRef.current.triggerAttackRelease(440 * 2 ** ((result.target - 69) / 12), TARGET_TONE_SECONDS);
    setHearing('target');
    clearTimeout(targetTimerRef.current);
    targetTimerRef.current = setTimeout(() => setHearing(current => (current === 'target' ? null : current)), TARGET_TONE_SECONDS * 1000);
  }, [stopHearing]);

  useEffect(() => () => {
    clearTimeout(hideTimerRef.current);
    clearTimeout(targetTimerRef.current);
    stopHearing();
    if (toneRef.current) {
      toneRef.current.dispose();
      toneRef.current = null;
    }
  }, [stopHearing]);
  // Fresh grades replace the result objects: keep inspecting the same note
  useEffect(() => {
    setInspect((current) => {
      if (!current) return current;
      const result = results.find(r => r.ref === current.result.ref);
      return result ? { ...current, result } : null;
    });
  }, [results]);

  // Switching tuning re-grades the take in hand, so the two can be compared
  const setTuning = useCallback((next) => {
    if (next === tuningRef.current) return;
    tuningRef.current = next;
    setTuningState(next);
    writeTuning(next);
    const c = controlsRef.current;
    logGrading({ user: c.user, fileName: c.fileName, description: `Grading tuning set to ${next === TUNINGS.just ? 'just' : 'equal'}`, metadata: { event: 'grade_tuning', tuning: next } });
    const take = takeRef.current;
    if (!take) return;
    take.tuning = next;
    take.slots = slotsFor(take, next);
    take.signature = '';
    regrade(take, { final: take.ended, full: true });
  }, [regrade]);

  // Whether playing the take back would play just the highlighted section
  const section = active ? highlightedSection(controls) : null;
  const sectionFrom = section ? section.from : null;
  const sectionTo = section ? section.to : null;
  const playsSection = useMemo(() => {
    const take = takeRef.current;
    return Boolean(hasTake && !listening && take?.ended && sectionFrom !== null && takeCovers(take, sectionFrom, sectionTo));
  }, [hasTake, listening, sectionFrom, sectionTo]);

  // Hovering over the highlighted bars graphs the whole stretch: the notes,
  // where the grader divided the singing between them, and the words. It
  // stays while the pointer is on the graph, so a note can be clicked.
  const [rangeHover, setRangeHover] = useState(null); // { rect } of the hovered system
  const rangeHoldRef = useRef(false);
  const rangeHideRef = useRef(null);
  const rangeInspecting = visible && results.length > 0 && !hoverPlay && !listening && sectionFrom !== null;
  const onRangeHover = useCallback((hit) => {
    clearTimeout(rangeHideRef.current);
    if (hit) {
      setRangeHover(hit);
    } else {
      rangeHideRef.current = setTimeout(() => {
        if (!rangeHoldRef.current) setRangeHover(null);
      }, INSPECT_HIDE_MS);
    }
  }, []);
  const holdRange = useCallback((on) => {
    rangeHoldRef.current = on;
    if (on) clearTimeout(rangeHideRef.current);
    else onRangeHover(null);
  }, [onRangeHover]);
  useEffect(() => {
    const osmd = getOsmdRef.current?.();
    if (!osmd || !rangeInspecting) return undefined;
    return watchRangeHover(osmd, onRangeHover);
  }, [rangeInspecting, fileName, onRangeHover]);
  useEffect(() => {
    if (!rangeInspecting) {
      rangeHoldRef.current = false;
      setRangeHover(null);
    }
  }, [rangeInspecting]);
  useEffect(() => () => clearTimeout(rangeHideRef.current), []);
  const range = useMemo(() => {
    const take = takeRef.current;
    // (re-grading in another tuning gives new results, drawn afresh)
    if (!rangeHover || !take?.ended || sectionFrom === null || results.length === 0) return null;
    // The highlighted bars in the take's own score time
    const view = rangeView(
      take.results,
      (sectionFrom - take.songOffset) * take.quarterSeconds,
      (sectionTo - take.songOffset) * take.quarterSeconds
    );
    if (!view) return null;
    const c = controlsRef.current;
    const osmd = c.getOsmd?.();
    return {
      ...view,
      notes: view.notes.map(n => ({ ...n, fifths: keyAtNote(osmd, n.ref, c.transposeSteps) })),
      rect: rangeHover.rect,
      bars: barsOf(c.selectedRange),
      tuning: take.tuning
    };
  }, [rangeHover, results, sectionFrom, sectionTo]);

  const clear = useCallback(() => {
    takeRef.current = null;
    setResults([]);
    setPasses([]);
    setHasTake(false);
  }, []);

  // The take as a WAV, with the analysis in an extra chunk for diagnosis
  const downloadTake = useCallback(() => {
    const take = takeRef.current;
    if (!take || !take.ended || take.chunks.length === 0) return;
    const samples = new Float32Array(take.kept);
    let offset = 0;
    take.chunks.forEach((chunk) => {
      samples.set(chunk.samples, offset);
      offset += chunk.samples.length;
    });
    const analysis = {
      kind: 'musiker-graded-take',
      sampleRate: take.sampleRate,
      hopSeconds: take.hopSeconds,
      lagSeconds: round(take.lagFrames * take.hopSeconds, 3),
      tuning: take.tuning,
      slots: take.slots.map(slot => ({
        start: round(slot.start, 4),
        end: round(slot.end, 4),
        midi: slot.notes.map(n => n.midi),
        tuning: slot.notes.map(n => n.tuning || [])
      })),
      // [context sample, f0, clarity, rms, pass, score time]
      frames: take.frames.map((f, i) => {
        const position = take.positions[i];
        return [f.sample, round(f.f0, 2), round(f.clarity, 3), round(f.rms, 5), position ? position.pass : -1, position ? round(position.time, 4) : null];
      }),
      results: take.results.map(({ pass, slot, grade, cents, sung, entry, settle }) => ({
        pass, slot, grade, cents,
        sung: sung === null ? null : round(sung, 2),
        entry: entry === null ? null : round(entry, 3),
        settle: settle === null ? null : round(settle, 3)
      }))
    };
    const blob = encodeWav(samples, take.sampleRate, take.gain, [{ id: 'mskr', text: JSON.stringify(analysis) }]);
    downloadBlob(blob, takeFileName(fileName, 'graded take'));
    logGrading({
      user: controlsRef.current.user,
      fileName,
      description: `Saved the graded take (${spoken(samples.length / take.sampleRate)})`,
      metadata: { event: 'save_take', seconds: Math.round(samples.length / take.sampleRate) }
    });
  }, [fileName]);

  return {
    available: hasMe,
    enabled,
    starting,
    error,
    listening,
    livePass,
    setEnabled,
    // Play with a count-in while grading, so the first note has a lead-in,
    // and a highlighted section once, so just that section is graded
    playOptions: enabled ? { countOff: true, once: true } : null,
    results,
    passes,
    hasGrades: results.length > 0,
    inspect,
    holdInspect,
    canHear: hasTake && !listening,
    hearing,
    hearTake,
    hearTarget,
    stopHearing,
    playRecording,
    playsSection,
    range,
    holdRange,
    visible,
    setVisible,
    clear,
    hasTake,
    downloadTake,
    tuning,
    setTuning,
    live,
    setLive
  };
}
